/**
 * WebAudio synth fallback tier for the UI sound system.
 *
 * When a real recording is unavailable (placeholder file or load failure),
 * each named UI sound is synthesised from oscillators / noise tailored to
 * that action.
 */

import { UiSoundName } from "./uiSfx.types";

// --- WebAudio synth fallback ---------------------------------------------

let cachedCtx: AudioContext | undefined;

function getCtx(): AudioContext | undefined {
  if (typeof window === "undefined") return undefined;
  if (cachedCtx && cachedCtx.state !== "closed") return cachedCtx;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return undefined;
  try {
    cachedCtx = new Ctor();
    return cachedCtx;
  } catch {
    return undefined;
  }
}

function tone(
  ctx: AudioContext,
  startTime: number,
  freq: number,
  durationSec: number,
  opts: {
    type?: OscillatorType;
    gain?: number;
    sweepTo?: number;
    attackSec?: number;
    releaseSec?: number;
  } = {}
) {
  const {
    type = "sine",
    gain = 0.18,
    sweepTo,
    attackSec = 0.005,
    releaseSec = Math.max(0.02, durationSec * 0.5),
  } = opts;

  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  if (sweepTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, sweepTo),
      startTime + durationSec
    );
  }

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, startTime);
  g.gain.exponentialRampToValueAtTime(gain, startTime + attackSec);
  g.gain.exponentialRampToValueAtTime(
    0.0001,
    startTime + Math.max(attackSec + 0.005, durationSec + releaseSec)
  );

  osc.connect(g).connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + durationSec + releaseSec + 0.02);
}

function noise(
  ctx: AudioContext,
  startTime: number,
  durationSec: number,
  opts: { gain?: number; lowPassFrom?: number; lowPassTo?: number } = {}
) {
  const { gain = 0.18, lowPassFrom = 2000, lowPassTo = 300 } = opts;
  const frames = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;

  const src = ctx.createBufferSource();
  src.buffer = buf;

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(lowPassFrom, startTime);
  filter.frequency.exponentialRampToValueAtTime(
    Math.max(60, lowPassTo),
    startTime + durationSec
  );

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, startTime);
  g.gain.exponentialRampToValueAtTime(gain, startTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startTime + durationSec);

  src.connect(filter).connect(g).connect(ctx.destination);
  src.start(startTime);
  src.stop(startTime + durationSec + 0.02);
}

const SYNTH: Record<UiSoundName, (ctx: AudioContext, t: number) => void> = {
  ping: (ctx, t) => {
    tone(ctx, t, 880, 0.08, { type: "sine", gain: 0.2 });
  },
  ding: (ctx, t) => {
    tone(ctx, t, 660, 0.09, { type: "sine", gain: 0.18 });
    tone(ctx, t + 0.09, 990, 0.12, { type: "sine", gain: 0.2 });
  },
  drop: (ctx, t) => {
    tone(ctx, t, 700, 0.14, { type: "sine", gain: 0.18, sweepTo: 320 });
  },
  settle: (ctx, t) => {
    tone(ctx, t, 600, 0.1, { type: "sine", gain: 0.18 });
    tone(ctx, t + 0.08, 400, 0.16, { type: "sine", gain: 0.2 });
  },
  click: (ctx, t) => {
    tone(ctx, t, 1200, 0.03, {
      type: "triangle",
      gain: 0.16,
      attackSec: 0.002,
      releaseSec: 0.02,
    });
  },
  whoosh: (ctx, t) => {
    noise(ctx, t, 0.18, { gain: 0.15, lowPassFrom: 2400, lowPassTo: 220 });
  },
  uplift: (ctx, t) => {
    tone(ctx, t, 440, 0.08, { type: "sine", gain: 0.17 });
    tone(ctx, t + 0.07, 587, 0.08, { type: "sine", gain: 0.17 });
    tone(ctx, t + 0.14, 784, 0.12, { type: "sine", gain: 0.2 });
  },
  pop: (ctx, t) => {
    tone(ctx, t, 260, 0.06, { type: "triangle", gain: 0.18 });
  },
  thud: (ctx, t) => {
    tone(ctx, t, 140, 0.12, { type: "sine", gain: 0.22, sweepTo: 90 });
  },
  warble: (ctx, t) => {
    tone(ctx, t, 450, 0.07, { type: "square", gain: 0.13 });
    tone(ctx, t + 0.08, 350, 0.07, { type: "square", gain: 0.13 });
    tone(ctx, t + 0.16, 450, 0.07, { type: "square", gain: 0.13 });
  },
  buzz: (ctx, t) => {
    tone(ctx, t, 200, 0.14, { type: "square", gain: 0.14 });
  },
};

export function playSynth(name: UiSoundName) {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  try {
    SYNTH[name](ctx, ctx.currentTime + 0.001);
  } catch {
    // do nothing
  }
}
